import { useCallback, useEffect, useRef, useState } from 'react';

export const TOAST_DURATION_MS = 2600;

/**
 * Show/auto-hide timing for <Toast />.
 *
 * showToast is a stable reference, so it is safe to list in the dependency
 * array of a useCallback/useEffect without re-running it on every render.
 *
 * @returns {{toast: ?object, showToast: function, hideToast: function}}
 */
function useToast(duration = TOAST_DURATION_MS) {
    const [toast, setToast] = useState(null); // { message, variant }
    const timeoutRef = useRef(null);

    const showToast = useCallback((message, variant = 'success') => {
        if (!message) return;
        setToast({ message, variant });
        // A second toast during the first one's window restarts the clock rather
        // than inheriting whatever was left of it.
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => setToast(null), duration);
    }, [duration]);

    const hideToast = useCallback(() => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        setToast(null);
    }, []);

    useEffect(() => () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
    }, []);

    return { toast, showToast, hideToast };
}

export default useToast;
